import { useFormContext } from 'react-hook-form';
import Select from 'react-select';
import {
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form.tsx';
import { Input } from '@/components/ui/input.tsx';
import { TPlant } from '@/features/plants/schemas/plants.schema.ts';
import { SelectStyles } from '../select-styles.ts';
import { useEffect } from 'react';
import { useStorageChemistriesQuery } from '@/features/plants/hooks/queries/use-storage-chemistries-query.ts';

export const TechnicalSpecification = ({ showTitle = false }: { showTitle?: boolean }) => {
  const form = useFormContext<TPlant>();
  const { data: storageChemistryOptions = [], isLoading: isLoadingChemistries } =
    useStorageChemistriesQuery();

  const setupType = form.watch('technicalSpecifications.setupType');

  const showProductionFields =
    setupType === 'production-only' || setupType === 'production-storage';
  const showStorageFields = setupType === 'storage-only' || setupType === 'production-storage';

  useEffect(() => {
    if (setupType === 'storage-only') return;

    const pvCapacity = form.getValues('technicalSpecifications.pvCapacity');
    const capacity = form.watch('technicalSpecifications.capacity');

    if (!pvCapacity && capacity) {
      form.setValue('technicalSpecifications.pvCapacity', capacity);
    }
  }, [setupType]);

  return (
    <div id="technicalSpecifications" className="space-y-4 rounded-lg bg-white p-4 py-4 pb-8">
      {showTitle && <h3 className="font-semibold">Technical Specifications</h3>}

      {showProductionFields && (
        <>
          <FormField
            control={form.control}
            name="technicalSpecifications.pvCapacity"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="required">PV Capacity (kWp)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    className="bg-white"
                    placeholder="Enter the plant capacity"
                    {...field}
                    value={field.value ?? undefined}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="technicalSpecifications.pvInvertingPower"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="">PV Inverter Power (kW)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    className="bg-white"
                    placeholder="Enter the inverter capacity"
                    {...field}
                    value={field.value ?? undefined}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}

      {showStorageFields && (
        <>
          <FormField
            control={form.control}
            name="technicalSpecifications.storageCapacity"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="required">Storage Capacity (kWh)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    min="0"
                    className="bg-white"
                    placeholder="Enter the storage capacity"
                    {...field}
                    value={field.value ?? undefined}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="technicalSpecifications.storageInvertingPower"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="">Storage Inverter Power (kW)</FormLabel>
                <FormControl>
                  <Input
                    type="number"
                    className="bg-white"
                    placeholder="Enter the storage inverter capacity"
                    {...field}
                    value={field.value ?? undefined}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />

          <FormField
            control={form.control}
            name="technicalSpecifications.storageChemistry"
            render={({ field }) => (
              <FormItem>
                <FormLabel className="">Storage Chemistry</FormLabel>
                <FormControl>
                  <Select
                    className="min-w-[200px]"
                    options={storageChemistryOptions}
                    value={
                      field.value
                        ? field.value.map((val: number) => {
                            const option = storageChemistryOptions.find(
                              (opt) => opt.value === val
                            );
                            return option ?? { value: val, label: String(val) };
                          })
                        : []
                    }
                    placeholder="Select storage chemistry"
                    isMulti
                    isLoading={isLoadingChemistries}
                    onChange={(selectedOptions) => {
                      const values =
                        (selectedOptions as { value: number }[])?.map(
                          (option) => option.value
                        ) || [];
                      field.onChange(values);
                    }}
                    styles={SelectStyles}
                  />
                </FormControl>
                <FormMessage />
              </FormItem>
            )}
          />
        </>
      )}
    </div>
  );
};
